import React from 'react';
import { radio } from './boards/radioPlayer';
import { Radio as RadioIcon, Square } from 'lucide-react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Bell, Search, User, LogOut, Settings, MessagesSquare, Mail, MonitorSmartphone } from 'lucide-react';
import CRMNotifications from './CRMNotifications';
import { QuickLaunch } from './QuickLaunch';
import { MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function MessageNotificationBell() {
  const [unread, setUnread] = React.useState(0);
  const navigate = useNavigate();
  
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const check = () => {
      fetch(`${API}/api/comms/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => setUnread(d.count || 0)).catch(() => {});
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <button data-testid="message-notification-bell" onClick={() => navigate('/company?tab=message')} className="relative p-2 rounded-lg hover:bg-white/40 transition-colors" title="Messages">
      <MessageSquare className="w-5 h-5 text-slate-700" />
      {unread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
          {unread}
        </span>
      )}
    </button>
  );
}

function MailBell() {
  const [unread, setUnread] = React.useState(0);
  const [allowed, setAllowed] = React.useState(false);
  const navigate = useNavigate();
  const sinceRef = React.useRef(new Date().toISOString());
  const seenRef = React.useRef(new Set(JSON.parse(localStorage.getItem('mail_notified_ids') || '[]')));

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const hdrs = { headers: { Authorization: `Bearer ${token}` } };
    const check = () => {
      fetch(`${API}/api/mail/status`, hdrs)
        .then(r => r.json()).then(d => { setAllowed(Boolean(d.allowed)); setUnread(d.unread || 0); }).catch(() => setTimeout(check, 5000));
    };
    const notify = async () => {
      const mode = localStorage.getItem('mail_alerts') || 'jobs';
      if (mode === 'off') { sinceRef.current = new Date().toISOString(); return; }
      try {
        const d = await (await fetch(`${API}/api/mail/new?since=${encodeURIComponent(sinceRef.current)}`, hdrs)).json();
        sinceRef.current = d.now || new Date().toISOString();
        for (const t of d.threads || []) {
          if (seenRef.current.has(t.id) || (mode === 'jobs' && !(t.chips || []).length)) continue;
          seenRef.current.add(t.id);
          localStorage.setItem('mail_notified_ids', JSON.stringify([...seenRef.current].slice(-200)));
          const job = (t.chips || []).map(c => c.ref).join(', ');
          const title = job ? `${job} — ${t.people[0] || 'New email'}` : t.people[0] || 'New email';
          const open = () => { window.focus(); navigate(`/mail?folder=inbox&thread=${t.id}`); };
          toast(title, { description: t.subject, duration: 15000, action: { label: 'Open', onClick: open } });
          if ('Notification' in window && Notification.permission === 'granted') {
            const n = new Notification(title, { body: t.subject, tag: t.id, icon: '/favicon.ico' });
            n.onclick = open;
          }
        }
      } catch { /* offline — try again next tick */ }
    };
    check();
    const interval = setInterval(() => {
      fetch(`${API}/api/mail/unread-count`, hdrs)
        .then(r => r.json()).then(d => setUnread(d.count || 0)).catch(() => {});
      notify();
    }, 60000);
    return () => clearInterval(interval);
  }, [navigate]);

  if (!allowed) return null;
  return (
    <button data-testid="mail-bell" onClick={() => navigate('/mail')} className="relative p-2 rounded-lg hover:bg-white/40 transition-colors" title={unread > 0 ? `${unread} unread emails` : 'Mail'}>
      <Mail className="w-5 h-5 text-slate-700" />
      {unread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold" data-testid="mail-bell-badge">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}

function DispatchChatBell() {
  const [state, setState] = React.useState({ count: 0, awaiting_reply: 0 });
  const navigate = useNavigate();

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const check = () => {
      fetch(`${API}/api/dispatch-chat/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => setState({ count: d.count || 0, awaiting_reply: d.awaiting_reply || 0 })).catch(() => {});
    };
    check();
    const interval = setInterval(check, 20000);
    return () => clearInterval(interval);
  }, []);

  const badge = state.awaiting_reply > 0 ? state.awaiting_reply : state.count;
  return (
    <button data-testid="dispatch-chat-bell" onClick={() => navigate('/dispatch-chat')} className="relative p-2 rounded-lg hover:bg-white/40 transition-colors" title={state.awaiting_reply > 0 ? 'A dispatch needs your reply' : 'Dispatch chat'}>
      <MessagesSquare className="w-5 h-5 text-slate-700" />
      {badge > 0 && (
        <span className={`absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full flex items-center justify-center text-[10px] text-white font-bold ${state.awaiting_reply > 0 ? 'bg-orange-600 animate-pulse' : 'bg-blue-600'}`} data-testid="dispatch-chat-badge">
          {badge}
        </span>
      )}
    </button>
  );
}
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';


function RadioNowPlaying() {
  const [st, setSt] = React.useState(radio.get());
  React.useEffect(() => radio.subscribe(setSt), []);
  if (!st.playing && !st.loading) return null;
  return (
    <button onClick={() => radio.stop()} title={`Playing ${st.station?.name} — click to stop`} data-testid="header-radio-pill"
      className="hidden md:inline-flex items-center gap-1.5 max-w-[180px] h-8 pl-2 pr-2.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700">
      <RadioIcon className="h-3.5 w-3.5 text-sky-300" />
      <span className="truncate">{st.station?.name}</span>
      <Square className="h-2.5 w-2.5 fill-current" />
    </button>
  );
}

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Successfully logged out');
    navigate('/login');
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      admin: 'Administrator',
      project_manager: 'Project Manager',
      field_worker: 'Field Worker',
      office_staff: 'Office Staff'
    };
    return roleNames[role] || role;
  };

  const getRoleBadgeClass = (role) => {
    const roleClasses = {
      admin: 'bg-blue-100 text-blue-800 border border-blue-300',
      project_manager: 'bg-green-100 text-green-800 border border-green-300',
      field_worker: 'bg-orange-100 text-orange-800 border border-orange-300',
      office_staff: 'bg-purple-100 text-purple-800 border border-purple-300'
    };
    return `px-2 py-1 rounded-lg text-xs font-semibold ${roleClasses[role] || 'bg-slate-100 text-slate-800 border border-slate-300'}`;
  };

  return (
    <header data-shell="header" className="bg-white/65 backdrop-blur-2xl border-b border-white/80 shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(23,52,96,0.06)] px-3 lg:px-6 py-3 lg:py-4 relative z-30">
      <div className="flex items-center justify-between">
        {/* Search - hidden on mobile */}
        <div className="hidden lg:flex flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search projects, quotes, clients..."
              className="w-full pl-10 pr-4 py-2 bg-white/80 border border-slate-200/90 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:ring-2 focus:ring-sky-200 focus:border-sky-400 outline-none transition-all duration-200 shadow-sm"
            />
          </div>
        </div>
        
        {/* Logo on mobile */}
        <div className="lg:hidden flex items-center ml-12">
          <img 
            src="/365-logo-dark.png" 
            alt="365 ISI Logo" 
            className="h-10 w-auto"
          />
          <h2 className="ml-2 text-xl font-bold text-slate-900 hidden sm:block">365Ops</h2>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-2 lg:space-x-4">
          <QuickLaunch user={user} />
          {/* Notifications - hidden on mobile */}
          {/* CRM Follow-up Notifications */}
          <div className="hidden lg:block">
            <CRMNotifications />
          </div>
          
          <RadioNowPlaying />

          {/* Dispatch group chat */}
          <DispatchChatBell />

          {/* Message Notifications */}
          <MessageNotificationBell />

          {/* Gmail inside ops */}
          <MailBell />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                data-testid="header-user-menu-trigger"
                className="flex items-center space-x-2 lg:space-x-3 hover:bg-white/80 px-2 lg:px-3 py-2 rounded-xl"
              >
                <div className="h-8 w-8 bg-gradient-to-br from-sky-600 to-blue-900 rounded-full flex items-center justify-center shadow-[0_4px_12px_rgba(29,78,216,0.3)]">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div className="text-left hidden lg:block">
                  <div className="text-sm font-semibold text-slate-900">{user?.full_name}</div>
                  <div className="text-xs text-slate-500 font-medium">{user?.email}</div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white/95 backdrop-blur-xl border-white/60 shadow-2xl">
              <DropdownMenuLabel className="text-slate-900">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-bold text-slate-900">{user?.full_name}</p>
                  <p className="text-xs text-slate-700 font-medium">{user?.email}</p>
                  <span className={getRoleBadgeClass(user?.role)}>
                    {getRoleDisplayName(user?.role)}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-300" />
              <DropdownMenuItem className="text-slate-900 hover:bg-blue-50 focus:bg-blue-50 font-medium cursor-pointer"
                onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-slate-900 hover:bg-blue-50 focus:bg-blue-50 font-medium cursor-pointer"
                onClick={() => navigate('/notifications')} data-testid="header-notifications-link">
                <Bell className="mr-2 h-4 w-4" />
                <span>Notifications</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-slate-900 hover:bg-blue-50 focus:bg-blue-50 font-medium cursor-pointer"
                onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-slate-900 hover:bg-blue-50 focus:bg-blue-50 font-medium cursor-pointer"
                onClick={() => navigate('/settings#sessions')} data-testid="header-sessions-link">
                <MonitorSmartphone className="mr-2 h-4 w-4" />
                <span>Where I'm signed in</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-300" />
              <DropdownMenuItem 
                className="text-red-600 hover:bg-red-50 focus:bg-red-50 font-semibold"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;